import clsx from "clsx";
import { useDispatch } from "react-redux";

import { AnimatedHeart } from "@chef/components";
import { useFavorites, useTrack } from "@chef/state-management/hooks";
import { openModal } from "@chef/state-management";

import { intl } from "./FavoriteButton.Intl";

interface FavoriteButtonProps {
  recipe: {
    recipeId: number;
    mainRecipeId: number | null;
    recipeName: string;
  };
  className?: string;
}

export const FavoriteButton = ({ recipe, className }: FavoriteButtonProps) => {
  const {
    isFavorited,
    isDisliked,
    toggleFavorite,
    toggleDislike,
    enableFavorites,
    isReady: favoritesReady,
  } = useFavorites();

  const dispatch = useDispatch();
  const track = useTrack();

  const handleFavoriteAction = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isDisliked(recipe)) {
      toggleDislike(recipe);
    }

    if (!enableFavorites) {
      dispatch(
        openModal({
          id: "RECIPE",
          recipeId: recipe.recipeId,
        }),
      );

      track("buttonClicked", {
        affiliation: "Frontend process",
        button_colour: "outlined",
        button_placement: "favorite-recipe-button",
        button_source: "code",
        button_text: `Favorite button clicked for recipe ${
          recipe.mainRecipeId || recipe.recipeId
        }`,
      });

      dispatch(openModal({ id: "RECIPE_ACTION" }));

      return;
    }

    toggleFavorite(recipe);
  };

  const ariaLabel = isFavorited(recipe)
    ? intl.REMOVE_RECIPE_FROM_FAVORITES(recipe.recipeName)
    : intl.ADD_RECIPE_TO_FAVORITES(recipe.recipeName);

  const disableButton = !favoritesReady && enableFavorites;

  return (
    <button
      onClick={handleFavoriteAction}
      name="favorite-recipe-button"
      aria-label={ariaLabel}
      disabled={disableButton}
      className={clsx(
        "flex items-center justify-center w-10 h-10 bg-white rounded-full hover:bg-white",
        className,
        disableButton && "cursor-not-allowed",
      )}
    >
      <AnimatedHeart
        id={recipe.recipeId.toString()}
        isFilled={isFavorited(recipe)}
        className="w-6 h-6 text-error mt-0.5"
      />
    </button>
  );
};
